import { useCallback, useState, useEffect, useMemo } from 'react';
import DocumentHeader from '@/components/DocumentHeader';
import Footer from '@/components/Footer';
import GlobalStyle from '@/styled/styled.default';
import { useTheme, LoadingWarren, Heading, Text } from '@simplywallst/ui-core';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchPage,
  getDefaultResponse,
  PAGE_SIZE,
} from '../../query/fetchPage';
import { SWS_HOST } from '@/constants';
import useReduxStateForStocks from '../../hooks/useReduxStateForStocks';
import { useMetaData } from '../../hooks/useMetaData';
import { flatten } from 'lodash';
import useUser from '@/hooks/useUser';
import track, { useTracking } from 'react-tracking';
import { BreadcrumbWrapper, TextHeader, DividendCTA } from './styled';
import Breadcrumb from '@/components/Breadcrumb';
import If from '@/components/If';
import ScrollLoader from '@/components/ScrollLoader';
import {
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
  ROUTE_STOCKS_HOME,
} from '@/constants/routes';
import DocumentSchema from '@/components/DocumentSchema';
import type {
  SearchResultsPage,
  Corporation,
  ListItem,
  ItemList,
} from 'schema-dts';
import loadable from '@loadable/component';
import { getStocksRouterAction } from '../../utils';
import Header from '../Header';
import { RouterLink } from '@redux-first-router-link';
import { LastUpdated } from '@/components/Header';
import { useStocksRules } from '../../hooks/useStocksRules';
import { DiscoverTabs } from '@/components/Nav/DiscoverTabs';

const TileList = loadable(() => import('@/components/TileList'), {
  resolveComponent: (c) => c.TileList,
});
const StockTable = loadable(() => import('../StockTable'));
const Pagination = loadable(() => import('@/components/Pagination'));

const GuestRegistrationModalOnScroll = loadable(
  () => import('@/components/GuestRegistrationModalOnScroll')
);

type StocksView = 'tile' | 'table';

const Stocks = () => {
  /** hooks */
  const theme = useTheme();

  const { trackEvent } = useTracking();

  const {
    data: user,
    mutate: mutateUser,
    isFetched: isUserFetched,
  } = useUser();

  const {
    market = 'global',
    industry = 'any',
    sortBy,
    pageQuery,
    redirect,
    preFetchCompany,
    onAdvancedFilters,
  } = useReduxStateForStocks();

  /**
   * Redirect
   */
  useEffect(() => {
    if (typeof sortBy !== 'undefined') return;
    const routerAction = getStocksRouterAction({
      market,
      industry,
      sortBy: 'market-cap-large',
      excludePageQuery: user.loggedIn,
    });
    redirect(routerAction);
  }, [market, industry, sortBy, user, redirect]);
  /**
   *
   */

  const isGuestUser = !user.loggedIn;

  const [page, setPage] = useState<number>(isGuestUser ? pageQuery : 1);

  useEffect(() => {
    if (isGuestUser) {
      setPage(pageQuery);
    }
  }, [pageQuery, isGuestUser]);

  const rules = useStocksRules(market, industry, sortBy);

  const {
    data = { pages: [getDefaultResponse()] },
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetched: isStocksFetched,
  } = useInfiniteQuery({
    queryKey: ['stock-page', rules, isGuestUser ? pageQuery : 1],
    queryFn: fetchPage,
    staleTime: 3600000,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return page < lastPage.meta.totalPages ? allPages.length + 1 : null;
    },
  });

  const [stocksView, setStocksView] = useState<StocksView>();

  useEffect(() => {
    if (isUserFetched && typeof stocksView === 'undefined' && user.loggedIn) {
      setStocksView(user.defaultStocksView);
    }
  }, [isUserFetched, user, stocksView]);

  useEffect(() => {
    if (!isGuestUser) {
      setPage(1);
    }
  }, [market, industry, isGuestUser]);

  const handleViewSelect = useCallback(
    (value: 'table' | 'tile') => {
      setStocksView(value);
      if (user.loggedIn) {
        mutateUser({
          settings: {
            stocks_view: value,
          },
        });
      }
    },
    [mutateUser, user]
  );

  const meta = useMetaData(
    market,
    industry,
    sortBy || 'market-cap-large',
    data.pages[0].companies
  );
  const { url, title, breadCrumbs, description, blurb, demonymCountry } = meta;

  const paginatedTitle =
    page > 1 && isGuestUser ? `Page ${page} - ${title}` : title;

  const handleAdvancedFilterClick = useCallback(() => {
    onAdvancedFilters(market, industry);
    trackEvent({
      action: 'click',
      subject: 'advanced-filter',
    });
  }, [market, industry, onAdvancedFilters, trackEvent]);

  const getRoutePayload = useCallback(
    (page: number) => {
      return {
        type:
          industry !== 'any'
            ? ROUTE_STOCKS_MARKET_INDUSTRY
            : ROUTE_STOCKS_MARKET,
        payload: {
          market,
          industry,
          sortBy,
          query: {
            page: page + '',
          },
        },
      };
    },
    [market, industry, sortBy]
  );

  const allCompanies = useMemo(
    () => flatten(data.pages.map((page) => page.companies)),
    [data]
  );

  return (
    <DiscoverTabs route={ROUTE_STOCKS_HOME}>
      <GlobalStyle background={theme.color.charcoal} />
      <DocumentHeader
        title={paginatedTitle}
        allowCrawlers={data.pages[0].companies.length > 0}
        description={description}
      >
        {data.pages[0].companies.length > 0 && (
          <link
            rel="canonical"
            href={`${url.toLowerCase()}${
              pageQuery === 1 ? '' : '?page=' + pageQuery
            }`}
          />
        )}
      </DocumentHeader>
      {RUNTIME_ENV === 'server' && (
        <DocumentSchema<SearchResultsPage>
          item={{
            '@context': 'https://schema.org',
            '@type': 'SearchResultsPage',
            identifier: `${url}#webpage`,
            url,
            mainEntity: [
              {
                '@type': 'ItemList',
                name: paginatedTitle,
                itemListOrder: 'https://schema.org/ItemListOrderAscending',
                itemListElement: data.pages[0].companies.map((n, i) => {
                  return {
                    '@type': 'ListItem',
                    position: i + 1,
                    item: {
                      '@type': 'Corporation',
                      name: n.shortName,
                      legalName: n.legalName,
                      tickerSymbol: n.tickerSymbol,
                      url: `${SWS_HOST}${n.slug}`,
                      logo: n.logoUrl,
                      foundingDate: n.yearFounded,
                      description: n.shortDescription,
                    } as Corporation,
                  } as ListItem;
                }),
              } as ItemList,
            ],
            isPartOf: {
              '@type': 'WebSite',
              url: 'https://simplywall.st',
              name: 'Simply Wall Street',
            },
          }}
        />
      )}
      <Header
        market={market}
        industry={industry}
        sortBy={sortBy}
        onAdvancedFilterClick={handleAdvancedFilterClick}
        stocksView={stocksView || 'table'}
        onViewSelect={handleViewSelect}
        totalRecords={
          data.pages[0].meta.totalRecords > PAGE_SIZE
            ? data.pages[0].meta.totalRecords
            : undefined
        }
      >
        <If match={industry !== 'any'}>
          <BreadcrumbWrapper>
            <Breadcrumb list={breadCrumbs} />
            {`/`}
          </BreadcrumbWrapper>
        </If>
        <TextHeader>
          <Heading
            as="h1"
            typography="megaStrong"
            data-cy-id="stock-header-title"
          >
            {paginatedTitle}
          </Heading>
          <LastUpdated />
          <Text typography="xSmall" color="whiteRGBA" opacity={0.5}>
            {blurb}
          </Text>
        </TextHeader>
        {sortBy === 'dividend-yield-high' && (
          <DividendCTA
            display="inline-grid"
            alignContent="center"
            justifyContent="center"
          >
            <span>
              Discover{` `}
              <RouterLink
                to={{
                  type: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
                  payload: {
                    id: '240709',
                    name: 'top-dividend-stocks',
                    market,
                  },
                }}
                onClick={() => {
                  trackEvent({
                    action: 'click',
                    subject: 'Top Quality Dividend Stocks',
                  });
                }}
              >
                Top Quality {demonymCountry} Dividend Stocks
              </RouterLink>
            </span>
          </DividendCTA>
        )}
      </Header>
      <ScrollLoader
        handleLoadRequest={() => {
          if (hasNextPage) {
            setPage(page + 1);
            fetchNextPage();
          }
        }}
        muteScrollEvent={isGuestUser}
      >
        {() =>
          typeof stocksView === 'undefined' || stocksView === 'table' ? (
            <StockTable
              companies={allCompanies}
              sortBy={sortBy}
              industry={industry}
              market={market}
              isFetching={isFetching}
              isFetched={isStocksFetched}
              onLongHover={preFetchCompany}
            />
          ) : (
            <TileList
              companies={allCompanies}
              onLongHover={preFetchCompany}
              isFetching={isFetching}
              isFetched={isStocksFetched}
            />
          )
        }
      </ScrollLoader>
      <If match={isFetching && isStocksFetched && !isGuestUser}>
        <LoadingWarren />
      </If>
      <If match={isGuestUser && isStocksFetched}>
        <Pagination
          currentPage={pageQuery}
          totalPages={data.pages[0].meta.totalPages}
          actionLink={getRoutePayload}
          loading={isFetching}
        />
      </If>
      <If match={isGuestUser}>
        <Footer />
      </If>
      <If match={isGuestUser}>
        <GuestRegistrationModalOnScroll />
      </If>
    </DiscoverTabs>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(Stocks) as typeof Stocks;
